import { maxInlineOperatorChartDescriptionBytes } from "../constants";
import type {
  HighchartsWrapperProps,
  OperatorChartData,
  OperatorChartDataAccess,
  OperatorCategoricalSummary,
  OperatorTimeSeriesSeries,
  OperatorTimeSeriesSummary,
} from "../types";
import { getOperatorTimeSeriesData } from "./getOperatorTimeSeriesData";

/**
 * Wraps a chart payload in an element that names what it is and which chart it belongs to, so
 * it is identifiable in Operator's page read and its JSON (series names are user text) is
 * escaped as element text.
 */
const describeChartPayload = (tag: "chart-data" | "chart-summary", chartId: string, json: string): string => {
  const element = document.createElement(tag);
  element.id = chartId;
  element.textContent = json;
  return element.outerHTML;
};

const getUtf8ByteLength = (value: string): number => {
  let byteLength = 0;

  for (const character of value) {
    const codePoint = character.codePointAt(0) || 0;

    if (codePoint <= 0x7f) {
      byteLength += 1;
    } else if (codePoint <= 0x7ff) {
      byteLength += 2;
    } else if (codePoint <= 0xffff) {
      byteLength += 3;
    } else {
      byteLength += 4;
    }
  }

  return byteLength;
};

/**
 * Keeps small charts inline in page context and replaces larger payloads with
 * a compact index that points Operator to the shared chart-data read tool.
 */
export const getOperatorChartDataAccess = (
  chartConfigOrData: HighchartsWrapperProps["chartConfig"] | OperatorChartData,
  chartId: string
): OperatorChartDataAccess | undefined => {
  const data =
    "type" in chartConfigOrData &&
    (chartConfigOrData.type === "time_series" || chartConfigOrData.type === "categorical")
      ? chartConfigOrData
      : getOperatorTimeSeriesData(chartConfigOrData);

  if (!data) {
    return undefined;
  }

  const inlineDescription = describeChartPayload("chart-data", chartId, JSON.stringify(data));
  if (getUtf8ByteLength(inlineDescription) <= maxInlineOperatorChartDescriptionBytes) {
    return {
      data,
      description: inlineDescription,
      requiresTool: false,
    };
  }

  const summarySeries: OperatorTimeSeriesSeries[] = data.series;
  const seriesSummary: Array<Pick<OperatorTimeSeriesSeries, "name" | "type" | "unit"> & { pointCount: number }> =
    summarySeries.map(({ name, type, unit, data: seriesData }) => ({
      name,
      type,
      ...(typeof unit === "string" && { unit }),
      pointCount: seriesData.length,
    }));
  const totalPointCount = summarySeries.reduce<number>(
    (total, { data: seriesData }) => total + seriesData.length,
    0
  );
  const summaryFields: Omit<OperatorTimeSeriesSummary, "type" | "xAxis"> = {
    id: chartId,
    seriesCount: data.series.length,
    totalPointCount,
    series: seriesSummary,
    hasMoreSeries: false,
    ...(data.metadata !== undefined ? { metadata: data.metadata } : {}),
    readWith: {
      tool: "get_chart_data",
      toolEntityId: chartId,
    },
  };
  const summary: OperatorTimeSeriesSummary | OperatorCategoricalSummary =
    data.type === "categorical"
      ? { ...summaryFields, type: "categorical_summary", xAxis: data.xAxis }
      : { ...summaryFields, type: "time_series_summary", xAxis: data.xAxis };
  let summaryDescription = describeChartPayload("chart-summary", chartId, JSON.stringify(summary));

  while (
    getUtf8ByteLength(summaryDescription) > maxInlineOperatorChartDescriptionBytes &&
    summary.series.length > 0
  ) {
    summary.series.pop();
    summary.hasMoreSeries = true;
    summaryDescription = describeChartPayload("chart-summary", chartId, JSON.stringify(summary));
  }

  return {
    data,
    description: summaryDescription,
    requiresTool: true,
  };
};
